/**
 * Developer (Traxo CAPTURE) Routes
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2019
 */

import moment from 'moment';
import { handleErrors } from '@/utils/errors.js';
import { store } from '@/state/store.js';

export const devRoutes = [
  // DASHBOARD
  {
    path: '/developers/:teamId/dashboard',
    name: 'dev.dashboard.index',
    component: () => import(/* webpackChunkName: "dev-dashboard" */ '@/views/devs/show.vue'),
    props: true,
    meta: {
      permission: 'read-dashboard',
      storeQuery: true,
      async beforeResolve(routeTo, routeFrom, next) {
        // set default date range
        if (!routeTo.query.startDate || !routeTo.query.endDate) {
          routeTo.query.startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
          routeTo.query.endDate = moment().format('YYYY-MM-DD');
        }

        store
          .dispatch('dashboard/parses', { query: routeTo.query })
          .then(() => {
            next();
          })
          .catch((err) => {
            handleErrors(err, next, 'Dashboard');
          });
      },
    },
  },

  // EMAILS
  {
    path: '/developers/:teamId/emails',
    name: 'dev.emails.index',
    component: () => import(/* webpackChunkName: "emails" */ '@/views/emails/index.vue'),
    meta: {
      permission: 'browse-emails',
      storeQuery: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Set a default sort
        if (!routeTo.query.sort) {
          routeTo.query.sort = 'created:desc';
        }

        store
          .dispatch('emails/index', { query: routeTo.query })
          .then(() => {
            next();
          })
          .catch((err) => {
            handleErrors(err, next, 'Emails');
          });
      },
    },
  },

  {
    path: '/developers/:teamId/emails/:id',
    name: 'dev.emails.show',
    component: () => import(/* webpackChunkName: "emails" */ '@/views/emails/show.vue'),
    props: true,
    meta: {
      permission: 'read-emails',
      beforeResolve(routeTo, routeFrom, next) {
        store
          .dispatch('emails/show', routeTo.params.id)
          .then((email) => {
            routeTo.params.email = email;
            next();
          })
          .catch((err) => {
            handleErrors(err, next, 'Email');
          });
      },
    },
  },

  // MAILBOXES
  {
    path: '/developers/:teamId/mailboxes',
    name: 'dev.mailboxes.index',
    component: () => import(/* webpackChunkName: "mailboxes" */ '@/views/mailboxes/index.vue'),
    meta: {
      permission: 'browse-mailboxes',
      storeQuery: true,
      beforeResolve(routeTo, routeFrom, next) {
        store
          .dispatch('mailboxes/fetch', { query: routeTo.query })
          .then(() => next())
          .catch((err) => {
            handleErrors(err, next, 'Mailboxes');
          });
      },
    },
    children: [
      {
        path: 'create',
        name: 'dev.mailboxes.create',
        component: () => import(/* webpackChunkName: "mailboxes" */ '@/views/mailboxes/create.vue'),
        meta: { permission: 'add-mailboxes' },
      },
    ],
  },
  {
    path: '/developers/:teamId/mailboxes/:id',
    name: 'dev.mailboxes.show',
    component: () => import(/* webpackChunkName: "mailboxes" */ '@/views/mailboxes/show.vue'),
    meta: {
      permission: 'read-mailboxes',
      beforeResolve(routeTo, routeFrom, next) {
        store
          .dispatch('mailboxes/read', { id: routeTo.params.id })
          .then((mailbox) => {
            routeTo.params.mailbox = mailbox;
            next();
          })
          .catch((err) => {
            handleErrors(err, next, 'Mailbox');
          });
      },
    },
    props: (route) => ({ mailbox: route.params.mailbox }),
  },

  // REPORTS //
  {
    path: '/developers/:teamId/reports',
    name: 'dev.reports.index',
    component: () => import(/* webpackChunkName: "reports" */ '@/views/reports/index.vue'),
    props: (route) => ({ query: route.query }),
    meta: {
      permission: 'add-reports',
      storeQuery: true,
      async beforeResolve(to, from, next) {
        try {
          await store.dispatch('reports/index', { query: to.query });
          next();
        } catch (err) {
          handleErrors(err, next, 'Reports');
        }
      },
    },
    children: [
      {
        path: ':id',
        name: 'dev.reports.show',
        component: () => import(/* webpackChunkName: "reports" */ '@/views/reports/show.vue'),
        meta: {
          permission: 'read-reports',
          beforeResolve(routeTo, routeFrom, next) {
            store
              .dispatch('reports/show', { id: routeTo.params.id })
              .then(() => {
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Reports');
              });
          },
        },
      },
    ],
  },

  // SETTINGS //
  {
    path: '/developers/:teamId/settings',
    name: 'dev.settings',
    component: () => import(/* webpackChunkName: "wrapper" */ '@/views/wrapper.vue'),
    meta: { permission: 'browse-settings' },
    redirect: { name: 'dev.settings.index' },
    children: [
      // General
      {
        path: 'general',
        name: 'dev.settings.index',
        component: () => import(/* webpackChunkName: "settings" */ '@/views/settings/index.vue'),
        meta: {
          permission: 'read-developers',
          beforeResolve(routeTo, routeFrom, next) {
            store
              .dispatch('me/showTeam', routeTo.params.teamId)
              .then(() => {
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'General Settings');
              });
          },
        },
      },

      // Users
      {
        path: 'users',
        name: 'dev.users.index',
        component: () => import(/* webpackChunkName: "users" */ '@/views/users/index.vue'),
        meta: {
          permission: 'browse-users',
          storeQuery: true,
          beforeResolve(routeTo, routeFrom, next) {
            store
              .dispatch('users/fetch', { query: routeTo.query })
              .then(() => {
                next();
              })
              .catch((error) => {
                handleErrors(error, next, 'Users');
              });
          },
        },
        children: [
          {
            path: 'create',
            name: 'dev.users.create',
            component: () => import(/* webpackChunkName: "users" */ '@/views/users/create.vue'),
            meta: { permission: 'add-users' },
          },
          {
            path: ':id',
            name: 'dev.users.show',
            component: () => import(/* webpackChunkName: "users" */ '@/views/users/show.vue'),
            meta: {
              permission: 'edit-users',
              beforeResolve(routeTo, routeFrom, next) {
                store
                  .dispatch('users/read', { id: routeTo.params.id })
                  .then((user) => {
                    routeTo.params.user = user;
                    next();
                  })
                  .catch((err) => {
                    handleErrors(err, next, 'User');
                  });
              },
            },
            props: (route) => ({ user: route.params.user }),
          },
        ],
      },

      // Applications
      {
        path: 'apps',
        name: 'dev.apps.index',
        component: () => import(/* webpackChunkName: "apps" */ '@/views/applications/index.vue'),
        meta: {
          permission: 'browse-applications',
          storeQuery: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Set a default sort
            if (!routeTo.query.sort) {
              routeTo.query.sort = 'created:desc';
            }

            store
              .dispatch('applications/fetch', { query: routeTo.query })
              .then(() => next())
              .catch((err) => {
                handleErrors(err, next, 'Applications');
              });
          },
        },
        children: [
          {
            path: 'create',
            name: 'dev.apps.create',
            component: () => import(/* webpackChunkName: "apps" */ '@/views/applications/create.vue'),
            meta: { permission: 'add-applications' },
          },
        ],
      },
      {
        path: 'apps/:id',
        name: 'dev.apps.show',
        component: () => import(/* webpackChunkName: "apps" */ '@/views/applications/show.vue'),
        meta: {
          permission: 'read-applications',
          beforeResolve(routeTo, routeFrom, next) {
            store
              .dispatch('applications/read', { id: routeTo.params.id })
              .then((application) => {
                routeTo.params.application = application;
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Application');
              });
          },
        },
        props: (route) => ({ application: route.params.application }),
      },

      // Webhooks
      {
        path: 'webhooks',
        name: 'dev.webhooks.index',
        component: () => import(/* webpackChunkName: "webhooks" */ '@/views/webhooks/index.vue'),
        meta: {
          permission: 'browse-webhooks',
          storeQuery: true,
          beforeResolve(routeTo, routeFrom, next) {
            store
              .dispatch('webhooks/fetch', { query: routeTo.query })
              .then(() => next())
              .catch((err) => {
                handleErrors(err, next, 'Webhooks');
              });
          },
        },
        children: [
          {
            path: 'create',
            name: 'dev.webhooks.create',
            component: () => import(/* webpackChunkName: "webhooks" */ '@/views/webhooks/create.vue'),
            meta: { permission: 'add-webhooks' },
          },
        ],
      },
      {
        path: 'webhooks/:id',
        name: 'dev.webhooks.show',
        component: () => import(/* webpackChunkName: "webhooks" */ '@/views/webhooks/show.vue'),
        meta: {
          permission: 'read-webhooks',
          beforeResolve(routeTo, routeFrom, next) {
            store
              .dispatch('webhooks/read', { id: routeTo.params.id })
              .then((webhook) => {
                routeTo.params.webhook = webhook;
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Webhook');
              });
          },
        },
        props: (route) => ({ webhook: route.params.webhook }),
      },

      // SSO Authentication settings
      {
        path: 'authentication',
        name: 'dev.authentication.index',
        component: () => import(/* webpackChunkName: "authentication" */ '@/views/sso/index.vue'),
        meta: {
          permission: 'browse-authentication',
        },
      },
    ],
  },
];
